/**
 * Role names from FusionAuth configuration
 * Based on: devtools/FusionAuth/Kickstart/kickstart.json
 */
import { env } from '@/config/env';
import { ASSISTING_ROLE_NAMES, ASSISTING_ROLE_LOOKUP_NAME, type AssistingRole } from '@/utils/complianceSectionUtils';
import { INTERNAL_USER_ORGANIZATION_MAP } from '@/constants';

export enum UserRole {
  // Hierarchical roles (with levels)
  User = 'user',
  Viewer = 'viewer',
  Uploader = 'uploader',
  Reviewer = 'reviewer',
  Administrator = 'administrator',

  // Specific user type roles (also hierarchical, at/near the floor - see ROLE_LEVELS)
  Driver = 'Driver',
  Monitor = 'Monitor',
  SpStaff = 'SpStaff',
  SpAdmin = 'SpAdmin',
}

/**
 * Every UserRole now participates in a single hierarchy (see ROLE_LEVELS) - kept as an alias
 * for call sites that predate the full-hierarchy model.
 */
export type HierarchicalRole = UserRole;

/**
 * Role level mapping for hierarchy comparison, per CP2 User Access Definition v3:
 * driver=0, monitor=0, spstaff=1, spadmin=2, viewer=3, uploader=4, reviewer=5, user=6,
 * administrator=7. This table is hand-synced with compliance-authorization-service's
 * Domain/Enums/UserRole.cs, its Infrastructure/Services/TokenService.cs, and its published
 * client package's RoleExtensions.cs - treat any hierarchy change as a change to all four.
 *
 * Driver/Monitor/SpStaff/SpAdmin keys are written as lowercase literals rather than
 * `[UserRole.Driver]` etc., since those enum values are PascalCase (matching FusionAuth role
 * names) but every lookup below normalizes the input role string via .toLowerCase() first.
 */
const ROLE_LEVELS: Record<string, number> = {
  driver: 0,
  monitor: 0,
  spstaff: 1,
  spadmin: 2,
  [UserRole.Viewer]: 3,
  [UserRole.Uploader]: 4,
  [UserRole.Reviewer]: 5,
  [UserRole.User]: 6,
  [UserRole.Administrator]: 7,
};

/**
 * Check if user has a specific role
 */
export const hasRole = (userRoles: string[], role: UserRole): boolean => {
  return userRoles.some((r) => r.toLowerCase() === role.toLowerCase());
};

/**
 * Check if user has any of the specified roles
 */
export const hasAnyRole = (userRoles: string[], roles: UserRole[]): boolean => {
  return roles.some((role) => hasRole(userRoles, role));
};

/**
 * Check if user is a provider submitter role (configurable via VITE_PROVIDER_SUBMITTER_ROLES)
 */
export const isProviderSubmitterRole = (userRoles: string[]): boolean => {
  const configuredRoles = env.providerSubmitterRoles;
  return userRoles.some((userRole) =>
    configuredRoles.some((configuredRole) =>
      userRole.toLowerCase() === configuredRole.toLowerCase(),
    ),
  );
};

/**
 * Check if user has Administrator role
 */
export const isAdmin = (userRoles: string[]): boolean => {
  return hasRole(userRoles, UserRole.Administrator);
};

/**
 * Check if user is a global admin: Administrator role in the ProductAndEngineering organization
 */
export const isGlobalAdmin = (userRoles: string[], organization: string): boolean => {
  const org = organization.trim().toLowerCase();
  return hasRole(userRoles, UserRole.Administrator) &&
    org === INTERNAL_USER_ORGANIZATION_MAP.productandengineering?.toLowerCase();
};

/**
 * Orgs whose Administrator gets full Service Provider CRUD (Create/Update/
 * Deactivate/Activate.AnyGroup), matching the authorization-service's
 * RolePermissionAssignmentConfiguration.cs seed. Configurable via
 * VITE_SERVICE_PROVIDER_ADMIN_ORGS so ops can adjust without a rebuild.
 */
const SERVICE_PROVIDER_ADMIN_ORGS: ReadonlySet<string> = new Set(
  env.serviceProviderAdminOrgs.map((o) => o.toLowerCase())
);

/**
 * Orgs that only ever get Service Provider Read.AnyGroup, regardless of
 * Administrator/User role. Configurable via VITE_SERVICE_PROVIDER_READONLY_ORGS.
 */
const SERVICE_PROVIDER_READONLY_ORGS: ReadonlySet<string> = new Set(
  env.serviceProviderReadonlyOrgs.map((o) => o.toLowerCase())
);

/**
 * True when the user can create/edit/activate/deactivate Service Providers:
 * Administrator role in one of the 5 CRUD orgs.
 */
export const canManageServiceProviders = (userRoles: string[], organization: string): boolean => {
  const org = organization.trim().toLowerCase();
  return hasRole(userRoles, UserRole.Administrator) && SERVICE_PROVIDER_ADMIN_ORGS.has(org);
};

/**
 * True when the user can at least see the Service Providers module:
 * Administrator or User role in a CRUD org (full access) or a read-only org
 * like SMT (view only — nav/route visible, mutating controls stay hidden).
 */
export const canViewServiceProviders = (userRoles: string[], organization: string): boolean => {
  const org = organization.trim().toLowerCase();
  if (!SERVICE_PROVIDER_ADMIN_ORGS.has(org) && !SERVICE_PROVIDER_READONLY_ORGS.has(org)) return false;
  return hasAnyRole(userRoles, [UserRole.Administrator, UserRole.User]);
};

/**
 * Per-org role requirement for the cascade-deactivate entry point. Matches the
 * ServiceProvider.CascadeDeactivate.AnyGroup permission grants seeded in
 * authorization-service: Administrator-only for Compliance, but Administrator OR the base User
 * role for ProductAndEngineering (per product clarification — "product/Eng users", not just
 * admins). This is an intentional v1 scope boundary, not a placeholder — widening it (new orgs,
 * new roles) requires a corresponding backend permission grant, not just a config change here.
 */
const SERVICE_PROVIDER_CASCADE_DEACTIVATE_ROLES_BY_ORG: ReadonlyMap<string, UserRole[]> = new Map(
  [
    [INTERNAL_USER_ORGANIZATION_MAP.compliance, [UserRole.Administrator]],
    [INTERNAL_USER_ORGANIZATION_MAP.productandengineering, [UserRole.Administrator, UserRole.User]],
  ]
    .filter((entry): entry is [string, UserRole[]] => Boolean(entry[0]))
    .map(([org, roles]) => [(org as string).toLowerCase(), roles as UserRole[]])
);

/**
 * True when the user can cascade-deactivate a Service Provider (deactivating the SP together
 * with all of its active users). Administrators outside these orgs, or non-admin users outside
 * ProductAndEngineering, still get plain (non-cascading) deactivate via canManageServiceProviders
 * — this only gates the cascade entry point.
 */
export const canCascadeDeactivateServiceProviders = (userRoles: string[], organization: string): boolean => {
  const org = organization.trim().toLowerCase();
  const allowedRoles = SERVICE_PROVIDER_CASCADE_DEACTIVATE_ROLES_BY_ORG.get(org);
  return allowedRoles !== undefined && hasAnyRole(userRoles, allowedRoles);
};

/**
 * True for an Administrator whose org is read-only-only for Service Provider
 * surfaces (currently SMT — no ServiceProvider or SP-scoped User AnyGroup
 * grants at all per the backend seed). Used to keep adjacent Admin-gated
 * surfaces (e.g. Service Provider Users) consistent with the same read-only
 * contract, instead of falling through on the generic isAdmin check.
 */
export const isServiceProviderReadOnlyOrgAdmin = (userRoles: string[], organization: string): boolean => {
  const org = organization.trim().toLowerCase();
  return hasRole(userRoles, UserRole.Administrator) && SERVICE_PROVIDER_READONLY_ORGS.has(org);
};

/**
 * Organizations whose users are treated as "internal" for cross-SP visibility
 * (Compliance Status Lookup surface). Kept lowercase for case-insensitive compare.
 */
const INTERNAL_VISIBILITY_ORGS: ReadonlySet<string> = new Set(
  [
    INTERNAL_USER_ORGANIZATION_MAP.compliance,
    INTERNAL_USER_ORGANIZATION_MAP.fieldops,
    INTERNAL_USER_ORGANIZATION_MAP.productandengineering,
  ]
    .filter((o): o is string => Boolean(o))
    .map((o) => o.toLowerCase())
);

/**
 * True when the caller is an *internal-viewpoint* user — Compliance / FieldOps /
 * ProductAndEngineering org with Administrator, Viewer, or User role. Used to
 * fork behavior between the SP-user viewpoint (SpStaff, own-SP scope) and the
 * internal viewpoint (multiple SPs, filtered by assigned states). Since only
 * internal users see Service Providers, this doubles as the SP-tab gate.
 *
 * Role set rationale — why the baseline `User` role is included:
 *   The Authorization Service seeds cross-org write grants for `User + Compliance`
 *   and `User + FieldOps` (Vehicle.Personal / Vehicle.Rental Create+Update.AnyGroup;
 *   see RolePermissionAssignmentConfiguration.cs assignments 30-37 and the
 *   AddVehicleAnyGroupCreateUpdateForFieldOpsAndCompliance migration). To exercise
 *   those grants a User has to navigate into another SP via the Compliance Status
 *   Lookup surface, which requires this predicate to return true for them.
 *   The org gate (`INTERNAL_VISIBILITY_ORGS`) still limits the exposure to the
 *   three internal FusionAuth orgs — a baseline User outside those orgs (e.g. an
 *   SP staffer with the "user" role in their SP's group) does NOT get internal
 *   visibility because `INTERNAL_VISIBILITY_ORGS.has(org)` returns false for them.
 *
 * If a future change grants cross-org permissions to a role not listed here, add
 * it to the array below; if a listed role loses its cross-org grants, remove it.
 * Keep the ordering: hierarchical high → low so a glance shows Admin/Viewer/User.
 */
export const hasInternalVisibility = (userRoles: string[], organization: string): boolean => {
  const org = organization.trim().toLowerCase();
  if (!INTERNAL_VISIBILITY_ORGS.has(org)) return false;
  return hasAnyRole(userRoles, [UserRole.Administrator, UserRole.Viewer, UserRole.User]);
};

/**
 * True when an SP-management caller should also see "View Compliance Status" on the
 * Service Provider detail page. The destination (Compliance Status Lookup's SP drill-in)
 * only exists for internal-visibility orgs, which is a narrower set than
 * SERVICE_PROVIDER_ADMIN_ORGS — some orgs can manage SPs without being internal-visibility,
 * for whom that destination has no Service Provider tab and is route-guarded off anyway.
 */
export const canViewServiceProviderComplianceStatus = (
  userRoles: string[],
  organization: string
): boolean => canManageServiceProviders(userRoles, organization) && hasInternalVisibility(userRoles, organization);

/**
 * True for anyone who should be able to reach the Compliance Status Lookup
 * surface (`/compliance-status-lookup/:tab`): provider-submitter roles,
 * internal-visibility orgs, or SpAdmin/SpStaff. Single source of truth for
 * both the sidebar nav item and the route guard — the route previously had
 * no guard at all and relied on the nav item being hidden, which let a
 * read-only-org Admin (e.g. SMT) reach the underlying D&M/Fleet views
 * (with their Edit/Manage Compliance/Add Vehicle/Deactivate controls) by
 * typing the URL directly.
 */
export const canAccessComplianceStatusLookup = (userRoles: string[], organization: string): boolean => {
  return (
    isProviderSubmitterRole(userRoles) ||
    hasInternalVisibility(userRoles, organization) ||
    hasAnyRole(userRoles, [UserRole.SpAdmin, UserRole.SpStaff])
  );
};

/**
 * Get the highest role level from user's roles
 */
export const getHighestRoleLevel = (userRoles: string[]): number => {
  return Math.max(
    ...userRoles.map((role) => ROLE_LEVELS[role.toLowerCase()] || 0),
    0
  );
};

/**
 * Check if user has a role at or above a certain hierarchical level.
 * Every UserRole now has a level (see ROLE_LEVELS), so this only throws for a
 * genuinely unrecognized role string.
 * @throws Error if minimumRole has no entry in ROLE_LEVELS
 */
export const hasMinimumRoleLevel = (userRoles: string[], minimumRole: HierarchicalRole): boolean => {
  const requiredLevel = ROLE_LEVELS[minimumRole.toLowerCase()];

  if (requiredLevel === undefined) {
    throw new Error(`Invalid role: ${minimumRole}. Not a recognized UserRole.`);
  }

  const userLevel = getHighestRoleLevel(userRoles);
  return userLevel >= requiredLevel;
};

/**
 * Check if user is a field user (Driver, Monitor, or configured provider submitter role)
 * These users should only see Compliance Management menus
 */
export const isFieldUser = (userRoles: string[]): boolean => {
  return hasAnyRole(userRoles, [UserRole.Driver, UserRole.Monitor]) || isProviderSubmitterRole(userRoles);
};

/**
 * Check if user has the Driver role.
 */
export const isDriver = (userRoles: string[]): boolean => {
  return hasRole(userRoles, UserRole.Driver);
};

/**
 * Check if user holds a role that may use the compliance review dashboard (administrator, user).
 * The legacy reviewer role is deprecated and no longer assignable, so it is not included.
 *
 * Role alone is not enough: `user` also exists inside SP groups, so callers must pair this with
 * the approving-org check (see useComplianceReviewAccess).
 */
export const hasComplianceReviewRole = (userRoles: string[]): boolean => {
  return hasAnyRole(userRoles, [UserRole.Administrator, UserRole.User]);
};

/**
 * True when the organization is ProductAndEngineering. P&E is not an approving org, so its
 * Administrators and Users review across all orgs; the evidence service's review queue still
 * hides admin-approval items from Users.
 */
export const isProductAndEngineeringOrg = (organization: string): boolean => {
  return organization.trim().toLowerCase() === INTERNAL_USER_ORGANIZATION_MAP.productandengineering?.toLowerCase();
};

/**
 * Maps the viewer's roles to the high-level assisting role used by SCA-gating
 * UI ('SP' / 'FO'). Returns null when the viewer is neither (e.g. a Driver in
 * Requirement Overview). Pass organizationName to enable the FO branch for
 * internal (FieldOps / Compliance / ProductAndEngineering) users.
 */
export const getViewerAssistingRole = (userRoles: string[], organizationName?: string): AssistingRole | null => {
  if (isProviderSubmitterRole(userRoles)) return 'SP';
  if (organizationName && hasInternalVisibility(userRoles, organizationName)) return 'FO';
  return null;
};

/**
 * Given the viewer's assisting role and the requirement's
 * `completionAssistingRoleName`, returns true when the viewer can submit
 * evidence for the requirement (SCA matches or is "Both"). Returns false
 * when the role doesn't match, and null when no SCA context applies.
 */
export const canViewerCompleteRequirement = (
  viewerAssistingRole: AssistingRole | null,
  requirementAssistingRoleName: string | null | undefined,
): boolean | null => {
  if (!viewerAssistingRole) return null;
  const required = ASSISTING_ROLE_LOOKUP_NAME[viewerAssistingRole].toLowerCase();
  const actual = requirementAssistingRoleName?.toLowerCase().trim();
  if (!actual) return false;
  return actual === required || actual === ASSISTING_ROLE_NAMES.BOTH.toLowerCase();
};

/**
 * Check if a role should be disabled based on mutually exclusive roles:
 * 1. Provider-submitter roles (env-configured via VITE_PROVIDER_SUBMITTER_ROLES,
 *    default SpStaff/SpAdmin) are mutually exclusive with Driver and Monitor —
 *    a management/office role and a field-crew role cannot be held together.
 * 2. Only one provider-submitter role may be selected at a time — SpAdmin
 *    already subsumes SpStaff's capabilities, so combining them is redundant.
 * 3. An account's role family (provider-submitter vs. field-crew) is
 *    immutable, not just "can't hold both at once" — pass the account's as-loaded
 *    `baselineRoles` (e.g. a Driver's original roles before any in-progress edits)
 *    so the opposite family stays disabled for the whole edit session, even after
 *    the user unchecks the conflicting role. Without this, deselecting Driver then
 *    selecting SpAdmin would briefly have no conflicting role in `selectedRoles`
 *    and the opposite-family option would incorrectly un-disable itself mid-edit.
 *    Defaults to `[]` for callers with no prior persisted roles (e.g. Add User),
 *    where this rule is a no-op.
 * Selected roles cannot be disabled.
 */
export const isRoleDisabled = (
  roleId: string,
  selectedRoles: string[],
  baselineRoles: string[] = [],
): boolean => {
  const isProviderSubmitter = (id: string) => isProviderSubmitterRole([id]);
  const isDriverOrMonitorRole = (id: string) =>
    ['driver', 'monitor'].includes(id.toLowerCase());

  if (selectedRoles.includes(roleId)) return false;

  const baselineIsProviderSubmitter = baselineRoles.some(isProviderSubmitter);
  const baselineIsFieldCrew = baselineRoles.some(isDriverOrMonitorRole);

  if (isProviderSubmitter(roleId) && baselineIsFieldCrew) return true;
  if (isDriverOrMonitorRole(roleId) && baselineIsProviderSubmitter) return true;

  if (isProviderSubmitter(roleId)) {
    if (selectedRoles.some(isDriverOrMonitorRole)) return true;
    // At-most-one provider-submitter role: disable other provider-submitter
    // options once one is already selected (e.g. SpStaff selected -> SpAdmin disabled).
    if (selectedRoles.some(isProviderSubmitter)) return true;
  }
  if (isDriverOrMonitorRole(roleId)) return selectedRoles.some(isProviderSubmitter);

  return false;
};